import {
  useCallback, useEffect, useRef,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { checkIfModalIsAlreadyConfirmed } from '~/_shared/components/modal/confirmation/confirmationModal.helpers';
import { PermanentConfirmStrategy } from '~/_shared/components/modal/confirmation/useConfirmationModal';
import { useQuery } from '~/_shared/utils/hooks/useQuery';
import { useStorageService } from '~/_shared/utils/storageService';
import { notNullsy } from '~/_shared/utils/typeGuards';
import { useModal } from '~/modal/useModal.hook';
import { useMapInfoDataSelector } from '~/store/mapInfo/mapInfo.selectors';
import { useModalIsOpenSelector } from '~/store/modal/modal.selectors';
import { MAP_TEMPLATES_URL_PARAM } from '../map.helpers';
import {
  isMapTemplate,
  MAP_TEMPLATES_AUTO_OPEN_ID,
} from '../mapTemplatesModal/mapTemplates.helpers';
import { useMapPageMapTemplatesCallbacks } from '../mapTemplatesModal/useMapPageMapTemplatesCallbacks.hook';

export const useAutoOpenMapTemplatesModal = () => {
  const query = useQuery();
  const openMapTemplatesUrlQuery = query.get(MAP_TEMPLATES_URL_PARAM);
  const templateFromUrl = isMapTemplate(openMapTemplatesUrlQuery) ? openMapTemplatesUrlQuery : undefined;
  const showMapTepmlatesModal = openMapTemplatesUrlQuery && !templateFromUrl;
  const { openModal: openMapTemplatesModal } = useModal('MapTemplates');
  const mapInfoData = useMapInfoDataSelector();
  const isAnyModalOpen = useModalIsOpenSelector();
  const storageService = useStorageService();
  const hasOpenedRef = useRef(false);
  const [, setSearchParams] = useSearchParams();

  const handleTemplateSelected = useMapPageMapTemplatesCallbacks();

  const removeMapTemplatesUrlParam = useCallback(() => {
    setSearchParams(current => {
      current.delete(MAP_TEMPLATES_URL_PARAM);
      return current;
    }, { replace: true });
  }, [setSearchParams]);

  const hasOptedOutOfAutoOpen = checkIfModalIsAlreadyConfirmed({
    id: MAP_TEMPLATES_AUTO_OPEN_ID,
    strategy: PermanentConfirmStrategy.Local,
  }, storageService);

  // Wait for any other modal to be dismissed first, rather than stacking on top of it.
  // Geocoding is intentionally not waited on here, so the modal opens right after all other modals close
  const canOpenTemplatesModal = notNullsy(mapInfoData) && !isAnyModalOpen && !hasOptedOutOfAutoOpen;

  useEffect(() => {
    if (hasOptedOutOfAutoOpen) {
      removeMapTemplatesUrlParam();
    }
  }, [hasOptedOutOfAutoOpen, removeMapTemplatesUrlParam]);

  useEffect(() => {
    if (hasOpenedRef.current) {
      return;
    }
    if (templateFromUrl) {
      hasOpenedRef.current = true;
      handleTemplateSelected(templateFromUrl);
      removeMapTemplatesUrlParam();
    }
    else if (showMapTepmlatesModal && canOpenTemplatesModal) {
      hasOpenedRef.current = true;
      openMapTemplatesModal({ isAutoOpened: true });
      removeMapTemplatesUrlParam();
    }
  }, [templateFromUrl, canOpenTemplatesModal, openMapTemplatesModal, handleTemplateSelected, showMapTepmlatesModal,
    removeMapTemplatesUrlParam]);
};
